import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { filter, find, get, isEmpty, pick } from 'lodash';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import ExportIcon from '@mui/icons-material/ImportExport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { IStudyDetailComment } from './_types';
import { useUser } from 'utils/hooks/useUser';
import { useActions } from 'utils/hooks/useActions';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import Button from 'components/Buttons/Button';
import { useLanguage } from 'utils/hooks/useLanguage';
import { Line } from 'components/Line/Line';
import { FINISHED } from 'constants/constants';
import { getCommentNameWithLogin } from 'utils/study';

const StudyDetailComment: React.FC<IStudyDetailComment> = ({
  comment,
  index,
  commentStatusAndPriorityCode,
  commentStatusCodeTranslation,
  priorityItems,
  allowedNextUserStatuses,
  formatDateTime,
  isLocked,
  setDialogState,
  setCommentItem,
  handleChangeStatus,
  commentsLength,
  renderedSupplementForDisplay = undefined,
}) => {
  const { t } = useTranslation('StudyDetail');
  const { hasRole, user } = useUser();
  const { currentLocale } = useLanguage();
  const { storeConfirmationData } = useActions();
  const { priorityAndStatusEnabled } = useAppInfo();

  const [expanded, setExpanded] = useState<boolean>(true);
  const toggleExpanded = () => setExpanded(!expanded);

  const createdByAuthor = getCommentNameWithLogin(get(comment, 'createdBy'));
  const modifiedByAuthor = getCommentNameWithLogin(get(comment, 'modifiedBy'));
  const commentCreatedByLoggedUser = get(comment, 'createdBy.username') === get(user, 'sub');
  const canSign = get(commentStatusAndPriorityCode, `${comment.id!}.signed`) === false;
  const isSigned = get(commentStatusAndPriorityCode, `${comment.id!}.signed`) === true;
  const isDeprecated = get(comment, 'deprecated', false);
  const isFinished = get(commentStatusAndPriorityCode, `${comment.id!}.status.type`) === FINISHED;
  const canCreateSupplement = isFinished && !isDeprecated;

  const commentPriorityCode = get(comment, 'commentPriorityCode', null);
  const commentPriorityCodeTranslation = get(
    find(priorityItems, { id: commentPriorityCode }),
    'label',
    '?',
  );
  const commentPriorityColor = get(
    find(priorityItems, { id: commentPriorityCode }),
    'color',
    'primary',
  );

  return (
    <div key={`comment-${comment.id}`}>
      {priorityAndStatusEnabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
          <Chip
            label={t(`PriorityStatusesConfig:is${!isDeprecated ? 'Not' : ''}Deprecated`)}
            color={isDeprecated ? 'error' : 'success'}
            variant="outlined"
            size="small"
          />
          {commentStatusCodeTranslation && (
            <Box sx={{ fontWeight: 'bold', ml: 1 }}>
              <Tooltip title={t('PriorityStatusesConfig:state')}>
                <Chip
                  label={commentStatusCodeTranslation}
                  color="default"
                  variant="outlined"
                  size="small"
                />
              </Tooltip>
            </Box>
          )}
          {priorityAndStatusEnabled && (
            <Box sx={{ fontWeight: 'bold', ml: 1 }}>
              <Chip
                label={commentPriorityCodeTranslation}
                variant="outlined"
                size="small"
                sx={{
                  color: (theme) =>
                    commentPriorityColor[0] === '#'
                      ? commentPriorityColor
                      : commentPriorityColor
                      ? get(theme, `palette.${commentPriorityColor}.main`)
                      : get(theme, `palette.primary.main`),
                }}
              />
            </Box>
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {get(comment, 'modifiedBy') && (
          <Tooltip
            title={
              <>
                <div>{t('modified')}:</div>
                <div>
                  {modifiedByAuthor} -{' '}
                  {format(new Date(get(comment, 'modifiedWhen') || ''), formatDateTime)}
                </div>
              </>
            }
          >
            <HistoryIcon sx={{ mr: 1, color: 'inherit' }} />
          </Tooltip>
        )}
        <Tooltip title={t('commentCreateTitle') as String}>
          <div>
            {get(comment, 'createdBy.email') ? (
              <Link
                href={`mailto: ${comment?.createdBy?.email}`}
                underline="none"
                sx={{ color: (theme) => theme.palette.success.main }}
              >
                {createdByAuthor}
              </Link>
            ) : (
              createdByAuthor
            )}{' '}
            - {format(new Date(get(comment, 'createdWhen') || ''), formatDateTime)}
          </div>
        </Tooltip>

        {!isLocked && (
          <>
            {filter(
              [
                {
                  title: 'commentUpdate',
                  children: <EditIcon />,
                  onClick: () => {
                    setDialogState(true);
                    setCommentItem(comment);
                  },
                  show:
                    hasRole('ROLE_COMMENTARY_ALL') ||
                    (commentCreatedByLoggedUser && hasRole('ROLE_COMMENTARY_MINE')),
                  disabled: priorityAndStatusEnabled && isDeprecated,
                },
                {
                  title: 'commentCreateSupplement',
                  children: <AddLinkIcon />,
                  onClick: () => {
                    setDialogState(true);
                    setCommentItem({ ...comment, hasCreateSupplement: true });
                  },
                  show:
                    (hasRole('ROLE_COMMENTARY_ALL') ||
                      (commentCreatedByLoggedUser && hasRole('ROLE_COMMENTARY_MINE'))) &&
                    canCreateSupplement,
                  disabled: priorityAndStatusEnabled && isDeprecated,
                },
                {
                  title: 'commentDelete',
                  children: <DeleteIcon />,
                  onClick: () => {
                    let shuldShowAnnounecments = false;
                    if (priorityAndStatusEnabled) {
                      if ('FINISHED' === get(comment, 'commentStatusCode', '')) {
                        shuldShowAnnounecments = true;
                      }
                    }
                    storeConfirmationData({
                      id: comment.id,
                      shuldShowAnnounecments,
                    });
                  },
                  show: hasRole('ROLE_COMMENTARY_ALL'),
                  disabled: false,
                },
                {
                  title: 'commentExport',
                  children: <ExportIcon />,
                  onClick: () => {
                    storeConfirmationData({
                      title: 'confirmExport',
                      id: comment.id,
                      text: comment.text,
                      confirmAction: 'confirmActionExportComment',
                    });
                  },
                  show: hasRole('ROLE_COMMENTARY_SEND'),
                  disabled: priorityAndStatusEnabled && !isFinished,
                },
                {
                  title: 'PriorityStatusesConfig:commentIsSigned',
                  children: <TaskAltIcon />,
                  show: isSigned,
                  disabled: true,
                },
              ],
              'show',
            ).map((button) => (
              <Tooltip key={button.title} title={t(button.title) as String}>
                <div>
                  <IconButton
                    {...pick(button, ['children', 'onClick'])}
                    sx={{ padding: (theme) => theme.spacing(1 / 4), ml: 1 }}
                    size="large"
                    disabled={button.disabled}
                  />
                </div>
              </Tooltip>
            ))}
            {isDeprecated
              ? []
              : allowedNextUserStatuses.map((item) => {
                  return (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleChangeStatus(item.code, comment);
                      }}
                      sx={{ ml: 1 }}
                    >
                      {get(item, `buttonText.${currentLocale}`, get(item, 'buttonText.cs', 'name'))}
                    </Button>
                  );
                })}
            {canSign && (
              <Tooltip title={t('onlyAuthorCanSignComment')}>
                <div>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      handleChangeStatus('manualStateHasToBeSigned', comment);
                    }}
                    disabled={!commentCreatedByLoggedUser}
                    sx={{ ml: 1 }}
                  >
                    {t('commentCanSign')}
                  </Button>
                </div>
              </Tooltip>
            )}
          </>
        )}
      </Box>
      <Box sx={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', mt: 1, lineHeight: 1 }}>
        {comment.text}
      </Box>
      {renderedSupplementForDisplay && !isEmpty(renderedSupplementForDisplay) && (
        <Accordion
          elevation={0}
          sx={{
            '&:before': {
              display: 'none',
            },
          }}
          defaultExpanded={true}
          onChange={() => toggleExpanded()}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel-with-supplements"
            id="panel-supplements"
            sx={{
              '&.Mui-expanded': {
                minHeight: 0,
              },
              '& .MuiAccordionSummary-content.Mui-expanded': {
                margin: '2px 0',
              },
            }}
          >
            <Typography variant="h6">
              <Box sx={{ marginLeft: 4 }}>
                {t(`supplement${renderedSupplementForDisplay.length > 1 ? 's' : ''}`)}
              </Box>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ marginLeft: 4 }}>{renderedSupplementForDisplay}</Box>
          </AccordionDetails>
        </Accordion>
      )}
      {++index < commentsLength ? <Line topMargin={2} bottomMargin={2} /> : null}
    </div>
  );
};

export default StudyDetailComment;
